import React, { useEffect, useState } from 'react';
import { PostCard } from './PostCard';
import { PersonCard } from './PersonCard';
import { linkedInPosts, peopleProfiles } from '../utils/mockData';
import { FilterIcon, UsersIcon, ArrowRightIcon, TrendingUpIcon, AlertCircleIcon, CheckCircleIcon, RefreshCwIcon, ChevronRightIcon, CalendarIcon, BarChart3Icon, ClockIcon, PlusCircleIcon, ThumbsUpIcon, MessageSquareIcon } from 'lucide-react';
import { PersonModal } from './PersonModal';
import { RecentActivity } from './RecentActivity';
interface DashboardProps {
  onPersonSelect: (personId: number) => void;
  onNavigateToTab: (tab: string) => void;
  onMarkInteresting: (personId: number) => void;
  onMarkNotRelevant: (personId: number) => void;
}
export const Dashboard: React.FC<DashboardProps> = ({
  onPersonSelect,
  onNavigateToTab,
  onMarkInteresting,
  onMarkNotRelevant
}) => {
  const [selectedPost, setSelectedPost] = useState<number | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<number | null>(null);
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [timeRange, setTimeRange] = useState('7days');
  const [activeInsightTab, setActiveInsightTab] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  // Simulate loading state when changing filters
  useEffect(() => {
    if (timeRange) {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 800);
    }
  }, [timeRange]);
  const handleViewEngagement = (postId: number) => {
    setSelectedPost(postId);
    setSelectedProfile(null);
    // Scroll to people list on mobile
    if (window.innerWidth < 768) {
      setTimeout(() => {
        const peopleList = document.getElementById('engaged-people-list');
        if (peopleList) {
          peopleList.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };
  const handlePersonSelect = (personId: number) => {
    setSelectedProfile(personId);
    setShowPersonModal(true);
  };
  const handleClosePersonModal = () => {
    setShowPersonModal(false);
  };
  const handleNavigateToMessages = (personId: number) => {
    onPersonSelect(personId);
    onNavigateToTab('messages');
  };
  const getEngagedPeople = (postId: number, filterBy?: string) => {
    let filteredPeople = peopleProfiles.filter(person => person.interactions.some(interaction => interaction.postId === postId));
    if (filterBy === 'high') {
      filteredPeople = filteredPeople.filter(p => p.relevance === 'high');
    } else if (filterBy === 'new') {
      // Filter for people with interactions in the last 3 days
      filteredPeople = filteredPeople.filter(p => p.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 3));
    }
    return filteredPeople;
  };
  const getHighIntentCount = (postId: number) => {
    return getEngagedPeople(postId, 'high').length;
  };
  const getNewEngagementCount = (postId: number) => {
    return getEngagedPeople(postId, 'new').length;
  };
  // Get intent signals summary
  const intentSignalsSummary = {
    total: peopleProfiles.length,
    high: peopleProfiles.filter(p => p.relevance === 'high').length,
    medium: peopleProfiles.filter(p => p.relevance === 'medium').length,
    low: peopleProfiles.filter(p => p.relevance === 'low').length,
    new: peopleProfiles.filter(p => p.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 2)).length
  };
  // Get recommended actions count
  const recommendedActionsCount = intentSignalsSummary.high + Math.floor(intentSignalsSummary.medium / 2);
  // Get post with highest engagement
  const topPost = [...linkedInPosts].sort((a, b) => b.likes + b.comments + b.shares - (a.likes + a.comments + a.shares))[0];
  // Get recent high-intent connections
  const recentHighIntentConnections = peopleProfiles.filter(p => p.relevance === 'high').sort((a, b) => {
    const getLatestInteraction = (person: (typeof peopleProfiles)[0]) => {
      if (person.interactions.length === 0) return 999;
      return Math.min(...person.interactions.map(i => {
        if (i.date.includes('day')) return parseInt(i.date) || 999;
        if (i.date.includes('week')) return parseInt(i.date) * 7 || 999;
        return 30;
      }));
    };
    return getLatestInteraction(a) - getLatestInteraction(b);
  }).slice(0, 3);
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-114">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-amber-50 via-amber-100 to-amber-50 rounded-lg shadow-sm border border-amber-200 p-6 mb-6" data-id="element-115">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center" data-id="element-116">
          <div className="mb-4 md:mb-0" data-id="element-117">
            <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-118">
              Welcome to LinkedIn Intent Monitor
            </h1>
            <p className="text-gray-700" data-id="element-119">
              Identify high-intent prospects and engage with them at the right
              moment.
            </p>
          </div>
          <div className="flex flex-wrap gap-3" data-id="element-120">
            <button onClick={() => onNavigateToTab('people')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#f9b30f] hover:bg-[#e0a40e]" data-id="element-121">
              <UsersIcon size={16} className="mr-2" data-id="element-122" />
              View Connections
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-123">
              <PlusCircleIcon size={16} className="mr-2" data-id="element-124" />
              Connect Account
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3 sm:gap-0" data-id="element-125">
        <div data-id="element-126">
          <h2 className="text-xl font-semibold text-gray-900" data-id="element-127">
            Your Intent Dashboard
          </h2>
          <p className="text-gray-500 text-sm mt-1" data-id="element-128">
            Overview of your LinkedIn engagement and intent signals
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-129">
          <select className="block pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] sm:text-sm rounded-md" value={timeRange} onChange={e => setTimeRange(e.target.value)} disabled={isLoading} data-id="element-130">
            <option value="7days" data-id="element-131">Last 7 days</option>
            <option value="30days" data-id="element-132">Last 30 days</option>
            <option value="90days" data-id="element-133">Last 90 days</option>
          </select>
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-134">
            <FilterIcon size={16} className="mr-2" data-id="element-135" />
            Filters
          </button>
        </div>
      </div>

      {/* Action Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-136">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 p-4" data-id="element-137">
          <div className="flex items-center justify-between mb-2" data-id="element-138">
            <h3 className="text-sm font-medium text-blue-800" data-id="element-139">
              High Intent Prospects
            </h3>
            <span className="p-1.5 rounded-full bg-blue-200" data-id="element-140">
              <UsersIcon size={16} className="text-blue-600" data-id="element-141" />
            </span>
          </div>
          <div className="text-2xl font-bold text-blue-800" data-id="element-142">
            {isLoading ? <div className="h-7 w-12 bg-blue-200 animate-pulse rounded" data-id="element-143"></div> : intentSignalsSummary.high}
          </div>
          <div className="text-xs text-blue-700 mt-1 mb-3" data-id="element-144">
            People showing strong buying signals
          </div>
          <button onClick={() => onNavigateToTab('people')} className="w-full text-center text-sm font-medium text-blue-700 bg-blue-200 hover:bg-blue-300 py-2 rounded-md transition-colors" data-id="element-145">
            View People
          </button>
        </div>
        <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg shadow-sm border border-amber-200 p-4" data-id="element-146">
          <div className="flex items-center justify-between mb-2" data-id="element-147">
            <h3 className="text-sm font-medium text-amber-800" data-id="element-148">
              Recommended Actions
            </h3>
            <span className="p-1.5 rounded-full bg-amber-200" data-id="element-149">
              <CheckCircleIcon size={16} className="text-amber-600" data-id="element-150" />
            </span>
          </div>
          <div className="text-2xl font-bold text-amber-800" data-id="element-151">
            {isLoading ? <div className="h-7 w-12 bg-amber-200 animate-pulse rounded" data-id="element-152"></div> : recommendedActionsCount}
          </div>
          <div className="text-xs text-amber-700 mt-1 mb-3" data-id="element-153">
            People to engage with today
          </div>
          <button onClick={() => onNavigateToTab('people')} className="w-full text-center text-sm font-medium text-amber-700 bg-amber-200 hover:bg-amber-300 py-2 rounded-md transition-colors" data-id="element-154">
            Take Action
          </button>
        </div>
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4" data-id="element-155">
          <div className="flex items-center justify-between mb-2" data-id="element-156">
            <h3 className="text-sm font-medium text-green-800" data-id="element-157">
              New Intent Signals
            </h3>
            <span className="p-1.5 rounded-full bg-green-200" data-id="element-158">
              <AlertCircleIcon size={16} className="text-green-600" data-id="element-159" />
            </span>
          </div>
          <div className="text-2xl font-bold text-green-800" data-id="element-160">
            {isLoading ? <div className="h-7 w-12 bg-green-200 animate-pulse rounded" data-id="element-161"></div> : intentSignalsSummary.new}
          </div>
          <div className="text-xs text-green-700 mt-1 mb-3" data-id="element-162">
            In the last 48 hours
          </div>
          <button onClick={() => onNavigateToTab('messages')} className="w-full text-center text-sm font-medium text-green-700 bg-green-200 hover:bg-green-300 py-2 rounded-md transition-colors" data-id="element-163">
            Create Messages
          </button>
        </div>
      </div>

      {/* Recent High-Intent Connections */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-164">
        <div className="flex justify-between items-center mb-4" data-id="element-165">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-166">
            Recent High-Intent Connections
          </h2>
          <button onClick={() => onNavigateToTab('people')} className="text-sm text-[#f9b30f] hover:text-[#e0a40e] font-medium flex items-center" data-id="element-167">
            View All
            <ChevronRightIcon size={16} className="ml-1" data-id="element-168" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-169">
          {isLoading ? Array(3).fill(0).map((_, i) => <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200 animate-pulse" data-id="element-170">
                    <div className="flex items-center mb-3" data-id="element-171">
                      <div className="w-10 h-10 rounded-full bg-gray-200 mr-3" data-id="element-172"></div>
                      <div data-id="element-173">
                        <div className="h-4 bg-gray-200 rounded w-24 mb-1" data-id="element-174"></div>
                        <div className="h-3 bg-gray-200 rounded w-32" data-id="element-175"></div>
                      </div>
                    </div>
                    <div className="h-3 bg-gray-200 rounded w-full mb-3" data-id="element-176"></div>
                    <div className="flex justify-between" data-id="element-177">
                      <div className="h-6 bg-gray-200 rounded w-20" data-id="element-178"></div>
                      <div className="h-6 bg-gray-200 rounded w-20" data-id="element-179"></div>
                    </div>
                  </div>) : recentHighIntentConnections.map(person => <div key={person.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-180">
                  <div className="flex items-center mb-3" data-id="element-181">
                    <img src={person.image} alt={person.name} className="w-10 h-10 rounded-full mr-3" data-id="element-182" />
                    <div data-id="element-183">
                      <h3 className="font-medium text-gray-900" data-id="element-184">
                        {person.name}
                      </h3>
                      <p className="text-xs text-gray-500" data-id="element-185">
                        {person.title} at {person.company}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center text-xs text-gray-500 mb-3" data-id="element-186">
                    <ClockIcon size={12} className="mr-1" data-id="element-187" />
                    {person.interactions[0]?.date || 'Recently active'}
                  </div>
                  <div className="flex justify-between" data-id="element-188">
                    <button onClick={() => handlePersonSelect(person.id)} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200" data-id="element-189">
                      View Profile
                    </button>
                    <button onClick={() => {
              onPersonSelect(person.id);
              onNavigateToTab('messages');
            }} className="text-xs px-2 py-1 bg-[#f9b30f] text-white rounded hover:bg-[#e0a40e]" data-id="element-190">
                      Send Message
                    </button>
                  </div>
                </div>)}
        </div>
      </div>

      {/* Activity Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-191">
        <div className="flex justify-between items-center mb-4" data-id="element-192">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-193">
            LinkedIn Activity Overview
          </h2>
          <div className="flex items-center" data-id="element-194">
            <span className="text-xs text-gray-500 mr-2" data-id="element-195">Last updated:</span>
            <span className="text-xs font-medium" data-id="element-196">Today, 10:30 AM</span>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-197">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-198">
            <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-199">
              <ThumbsUpIcon size={14} className="mr-1.5 text-blue-500" data-id="element-200" />
              Total Engagements
            </div>
            <div className="text-xl font-bold text-gray-900 flex items-center" data-id="element-201">
              {isLoading ? <div className="h-7 w-16 bg-gray-200 animate-pulse rounded" data-id="element-202"></div> : <>
                  243
                  <span className="ml-2 text-xs text-green-600 flex items-center" data-id="element-203">
                    <TrendingUpIcon size={12} className="mr-0.5" data-id="element-204" />
                    +12%
                  </span>
                </>}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-205">
            <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-206">
              <MessageSquareIcon size={14} className="mr-1.5 text-purple-500" data-id="element-207" />
              Response Rate
            </div>
            <div className="text-xl font-bold text-gray-900 flex items-center" data-id="element-208">
              {isLoading ? <div className="h-7 w-16 bg-gray-200 animate-pulse rounded" data-id="element-209"></div> : <>
                  18%
                  <span className="ml-2 text-xs text-green-600 flex items-center" data-id="element-210">
                    <TrendingUpIcon size={12} className="mr-0.5" data-id="element-211" />
                    +3%
                  </span>
                </>}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-212">
            <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-213">
              <BarChart3Icon size={14} className="mr-1.5 text-indigo-500" data-id="element-214" />
              Avg. Engagement
            </div>
            <div className="text-xl font-bold text-gray-900" data-id="element-215">
              {isLoading ? <div className="h-7 w-16 bg-gray-200 animate-pulse rounded" data-id="element-216"></div> : '6.2'}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200" data-id="element-217">
            <div className="flex items-center text-sm text-gray-500 mb-1" data-id="element-218">
              <CalendarIcon size={14} className="mr-1.5 text-green-500" data-id="element-219" />
              Best Day
            </div>
            <div className="text-xl font-bold text-gray-900" data-id="element-220">
              {isLoading ? <div className="h-7 w-16 bg-gray-200 animate-pulse rounded" data-id="element-221"></div> : 'Friday'}
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-222">
          <div className="text-sm text-gray-600" data-id="element-223">
            <span className="font-medium text-gray-900" data-id="element-224">Key Insight:</span>{' '}
            Friday posts receive the highest engagement and generate the most
            high-intent signals. Consider focusing your outreach efforts after
            Friday posts to maximize response rates.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-225">
        <div className="lg:col-span-1" data-id="element-226">
          <div className="mb-4 flex justify-between items-center" data-id="element-227">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-228">
              Recent LinkedIn Posts
            </h2>
            <button onClick={() => onNavigateToTab('people')} className="text-sm text-[#f9b30f] hover:text-[#e0a40e] font-medium flex items-center" data-id="element-229">
              View All People
              <ArrowRightIcon size={16} className="ml-1" data-id="element-230" />
            </button>
          </div>
          {isLoading ? <div className="space-y-4" data-id="element-231">
              {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse" data-id="element-232">
                  <div className="flex items-center mb-3" data-id="element-233">
                    <div className="w-10 h-10 rounded-full bg-gray-200 mr-3" data-id="element-234"></div>
                    <div className="flex-1" data-id="element-235">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" data-id="element-236"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2" data-id="element-237"></div>
                    </div>
                  </div>
                  <div className="h-12 bg-gray-200 rounded mb-4" data-id="element-238"></div>
                  <div className="flex justify-between items-center" data-id="element-239">
                    <div className="flex space-x-4" data-id="element-240">
                      <div className="h-4 bg-gray-200 rounded w-8" data-id="element-241"></div>
                      <div className="h-4 bg-gray-200 rounded w-8" data-id="element-242"></div>
                      <div className="h-4 bg-gray-200 rounded w-8" data-id="element-243"></div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-24" data-id="element-244"></div>
                  </div>
                </div>)}
            </div> : <div className="space-y-4" data-id="element-245">
              {linkedInPosts.map(post => <PostCard key={post.id} post={post} onViewEngagement={handleViewEngagement} isSelected={selectedPost === post.id} highIntentCount={getHighIntentCount(post.id)} newEngagementCount={getNewEngagementCount(post.id)} data-id="element-246" />)}
            </div>}
          {/* Recent Activity Feed */}
          <div className="mt-6" data-id="element-247">
            <RecentActivity data-id="element-248" />
          </div>
        </div>
        <div id="engaged-people-list" className="lg:col-span-2" data-id="element-249">
          {selectedPost ? <>
              <div className="mb-4" data-id="element-250">
                <div className="flex justify-between items-center" data-id="element-251">
                  <h2 className="text-lg font-medium text-gray-900" data-id="element-252">
                    People Who Engaged
                    <span className="ml-2 text-sm font-normal text-gray-500" data-id="element-253">
                      (
                      {getEngagedPeople(selectedPost, activeInsightTab === 'all' ? undefined : activeInsightTab).length}{' '}
                      people)
                    </span>
                  </h2>
                  <button onClick={() => setSelectedPost(null)} className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700" data-id="element-254">
                    Clear Selection
                  </button>
                </div>
                <div className="mt-2 border-b border-gray-200" data-id="element-255">
                  <nav className="-mb-px flex space-x-6" data-id="element-256">
                    <button onClick={() => setActiveInsightTab('all')} className={`whitespace-nowrap py-2 px-1 border-b-2 text-sm font-medium ${activeInsightTab === 'all' ? 'border-[#f9b30f] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-257">
                      All ({getEngagedPeople(selectedPost).length})
                    </button>
                    <button onClick={() => setActiveInsightTab('high')} className={`whitespace-nowrap py-2 px-1 border-b-2 text-sm font-medium ${activeInsightTab === 'high' ? 'border-[#f9b30f] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-258">
                      High Intent (
                      {getEngagedPeople(selectedPost, 'high').length})
                    </button>
                    <button onClick={() => setActiveInsightTab('new')} className={`whitespace-nowrap py-2 px-1 border-b-2 text-sm font-medium ${activeInsightTab === 'new' ? 'border-[#f9b30f] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-259">
                      New ({getEngagedPeople(selectedPost, 'new').length})
                    </button>
                  </nav>
                </div>
              </div>
              {isLoading ? <div className="space-y-4" data-id="element-260">
                  {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse" data-id="element-261">
                      <div className="flex items-center mb-3" data-id="element-262">
                        <div className="w-12 h-12 rounded-full bg-gray-200 mr-3" data-id="element-263"></div>
                        <div className="flex-1" data-id="element-264">
                          <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" data-id="element-265"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2" data-id="element-266"></div>
                        </div>
                      </div>
                      <div className="h-4 bg-gray-200 rounded mb-3 w-2/3" data-id="element-267"></div>
                      <div className="h-16 bg-gray-200 rounded mb-4" data-id="element-268"></div>
                      <div className="flex justify-between items-center pt-2" data-id="element-269">
                        <div className="h-4 bg-gray-200 rounded w-24" data-id="element-270"></div>
                        <div className="flex space-x-2" data-id="element-271">
                          <div className="h-6 bg-gray-200 rounded w-16" data-id="element-272"></div>
                          <div className="h-6 bg-gray-200 rounded w-16" data-id="element-273"></div>
                        </div>
                      </div>
                    </div>)}
                </div> : getEngagedPeople(selectedPost, activeInsightTab === 'all' ? undefined : activeInsightTab).length > 0 ? <div className="space-y-4" data-id="element-274">
                  {getEngagedPeople(selectedPost, activeInsightTab === 'all' ? undefined : activeInsightTab).map(person => <PersonCard key={person.id} person={person} onSelect={handlePersonSelect} isSelected={selectedProfile === person.id} onMarkInteresting={onMarkInteresting} onMarkNotRelevant={onMarkNotRelevant} data-id="element-275" />)}
                </div> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-276">
                  <UsersIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-277" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-278">
                    No{' '}
                    {activeInsightTab !== 'all' ? activeInsightTab + ' intent ' : ''}
                    engagement found
                  </h3>
                  <p className="text-gray-500" data-id="element-279">
                    {activeInsightTab !== 'all' ? `Try selecting a different filter or check other posts.` : `This post hasn't received any engagement yet.`}
                  </p>
                </div>}
            </> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8" data-id="element-280">
              <div className="text-center mb-6" data-id="element-281">
                <UsersIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-282" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-283">
                  Select a post to view engagement
                </h3>
                <p className="text-gray-500 mb-6" data-id="element-284">
                  Click "View Engagement" on any post to see who interacted with
                  it
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" data-id="element-285">
                <h3 className="font-medium text-blue-800 mb-2" data-id="element-286">
                  Pro Tip: Identify Intent Signals
                </h3>
                <p className="text-sm text-blue-700 mb-4" data-id="element-287">
                  Look for these engagement patterns that indicate buying
                  intent:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-id="element-288">
                  <div className="flex items-start" data-id="element-289">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-290" />
                    <span data-id="element-291">Multiple engagements within a short period</span>
                  </div>
                  <div className="flex items-start" data-id="element-292">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-293" />
                    <span data-id="element-294">
                      Comments with specific questions about solutions
                    </span>
                  </div>
                  <div className="flex items-start" data-id="element-295">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-296" />
                    <span data-id="element-297">Engagement with product/service related content</span>
                  </div>
                  <div className="flex items-start" data-id="element-298">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-299" />
                    <span data-id="element-300">Profile views combined with content engagement</span>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-center" data-id="element-301">
                <button onClick={() => handleViewEngagement(topPost.id)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#f9b30f] hover:bg-[#e0a40e]" data-id="element-302">
                  <RefreshCwIcon size={16} className="mr-2" data-id="element-303" />
                  View Latest Engagement
                </button>
              </div>
            </div>}
        </div>
      </div>

      {showPersonModal && selectedProfile && <PersonModal personId={selectedProfile} onClose={handleClosePersonModal} onNavigateToMessages={handleNavigateToMessages} onMarkInteresting={onMarkInteresting} onMarkNotRelevant={onMarkNotRelevant} data-id="element-304" />}
    </div>;
};